import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { listAssets, queueDepth, type AssetListFilters, type AssetListItem, type QueueDepth } from "../lib/api";

const THEMES = ["", "sleep", "wind_down", "calm_anxiety", "focus", "morning_energy", "reset_breath", "grounding", "self_compassion"];
const VOICES = ["", "f_warm_low", "f_warm_bright", "f_soft_neutral", "m_warm_low", "m_calm_grounded", "m_soft_bright"];
const LENGTHS = ["", "3", "5", "10", "15", "20"];

export default function Library() {
  const [params, setParams] = useSearchParams();
  const filters: AssetListFilters = useMemo(() => ({
    theme:     params.get("theme")     ?? "",
    voice:     params.get("voice")     ?? "",
    length:    params.get("length")    ?? "",
    tier:      (params.get("tier") as "free" | "paid" | "") ?? "",
    quality:   (params.get("quality") as "passed" | "failed" | "unreviewed" | "") ?? "",
    published: (params.get("published") as "true" | "false" | "") ?? "",
  }), [params]);

  const [items, setItems]     = useState<AssetListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [depth, setDepth]     = useState<QueueDepth | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([listAssets({ ...filters, limit: 100 }), queueDepth()])
      .then(([r, d]) => { if (!cancelled) { setItems(r.items); setDepth(d); } })
      .catch(e => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters]);

  function setFilter(name: keyof AssetListFilters, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value); else next.delete(name);
    setParams(next, { replace: true });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold">Library</h1>
        <div className="text-xs text-slate-500">{items.length} loaded</div>
        {depth && (
          <div className="ml-auto flex gap-3 text-xs text-slate-400">
            <Pill label="queued"  v={depth.queued}  tone={depth.queued ? "warn" : "muted"} />
            <Pill label="running" v={depth.running} tone={depth.running ? "warn" : "muted"} />
            <Pill label="failed"  v={depth.failed}  tone={depth.failed ? "bad"  : "muted"} />
            <Pill label="needs review" v={depth.needs_review} tone={depth.needs_review ? "bad" : "muted"} />
            <Pill label="done"    v={depth.done}    tone="muted" />
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 text-sm">
        <Select label="Theme"     value={filters.theme!}     options={THEMES} onChange={v => setFilter("theme", v)} />
        <Select label="Voice"     value={filters.voice!}     options={VOICES} onChange={v => setFilter("voice", v)} />
        <Select label="Length"    value={filters.length!}    options={LENGTHS} onChange={v => setFilter("length", v)} />
        <Select label="Tier"      value={filters.tier!}      options={["", "free", "paid"]} onChange={v => setFilter("tier", v)} />
        <Select label="Quality"   value={filters.quality!}   options={["", "passed", "failed", "unreviewed"]} onChange={v => setFilter("quality", v)} />
        <Select label="Published" value={filters.published!} options={["", "true", "false"]} onChange={v => setFilter("published", v)} />
      </div>

      {error && <div className="text-sm text-rainfall-bad">{error}</div>}

      <div className="overflow-x-auto border border-slate-800 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-900/60 text-xs uppercase tracking-wider text-slate-400">
            <tr>
              <Th>Asset</Th>
              <Th>Theme</Th>
              <Th>Voice</Th>
              <Th>Len</Th>
              <Th>Tier</Th>
              <Th>Dur</Th>
              <Th>Plays 7d</Th>
              <Th>Compl 7d</Th>
              <Th>Quality</Th>
              <Th>Published</Th>
            </tr>
          </thead>
          <tbody>
            {items.map(a => (
              <tr key={a.assetId} className="border-t border-slate-900 hover:bg-slate-900/60">
                <td className="px-3 py-2 font-mono text-xs">
                  <Link to={`/assets/${a.assetId}`} className="text-rainfall-accent hover:underline">
                    {a.assetId.slice(0, 8)}…
                  </Link>
                </td>
                <td className="px-3 py-2">{a.theme}</td>
                <td className="px-3 py-2">{a.voice}</td>
                <td className="px-3 py-2">{a.length}m</td>
                <td className="px-3 py-2"><TierBadge tier={a.tier} /></td>
                <td className="px-3 py-2">{fmtDuration(a.durationSec)}</td>
                <td className="px-3 py-2">{a.plays7d}</td>
                <td className="px-3 py-2">{a.completionRate7d == null ? "—" : `${Math.round(a.completionRate7d * 100)}%`}</td>
                <td className="px-3 py-2"><QualityBadge q={a.qualityPassed} /></td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {a.publishedAt ? new Date(a.publishedAt).toLocaleDateString() : <span className="text-rainfall-warn">no</span>}
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-500">No assets match.</td></tr>
            )}
          </tbody>
        </table>
        {loading && <div className="px-3 py-3 text-xs text-slate-500">Loading…</div>}
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="px-3 py-2 text-left font-medium">{children}</th>;
}

function Select({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (v: string) => void }) {
  return (
    <label className="flex items-center gap-2 bg-slate-900 border border-slate-800 rounded px-2 py-1">
      <span className="text-xs text-slate-500">{label}</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="bg-transparent text-sm outline-none"
      >
        {options.map(o => <option key={o} value={o}>{o === "" ? "any" : o}</option>)}
      </select>
    </label>
  );
}

function TierBadge({ tier }: { tier: "free" | "paid" }) {
  const cls = tier === "paid"
    ? "bg-yellow-500/15 text-yellow-200 border-yellow-500/30"
    : "bg-slate-700/30 text-slate-300 border-slate-600/40";
  return <span className={`text-xs px-2 py-0.5 rounded border ${cls}`}>{tier}</span>;
}

function QualityBadge({ q }: { q: number | null }) {
  if (q == null) return <span className="text-slate-500 text-xs">—</span>;
  if (q === 1)  return <span className="text-xs px-2 py-0.5 rounded border bg-emerald-500/15 text-emerald-300 border-emerald-500/30">passed</span>;
  return <span className="text-xs px-2 py-0.5 rounded border bg-rose-500/15 text-rose-300 border-rose-500/30">failed</span>;
}

function Pill({ label, v, tone }: { label: string; v: number; tone: "muted" | "warn" | "bad" }) {
  const cls = tone === "bad"
    ? "text-rainfall-bad"
    : tone === "warn"
    ? "text-rainfall-warn"
    : "text-slate-500";
  return (
    <span className={cls}>
      {label}: <span className="font-mono">{v}</span>
    </span>
  );
}

function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
